import { ANY_SETTINGS_VIEW_KEYS, PAYROLL_PREP_VIEW_KEYS } from '@attendance/shared';
import {
  ArrowLeftRight,
  BookUser,
  Bus,
  Building2,
  CalendarCheck,
  CalendarClock,
  CalendarRange,
  CheckCheck,
  ClipboardCheck,
  Clock,
  FileCheck,
  FileCheck2,
  History,
  IdCard,
  KeyRound,
  Landmark,
  MapPin,
  MessageSquarePlus,
  MonitorSmartphone,
  Palmtree,
  Gauge,
  ScanFace,
  ServerCog,
  SettingsIcon,
  Table2,
  Tags,
  Truck,
  UserCheck,
  Users,
  Wallet,
  ListChecks,
} from 'lucide-react';
import type { NavGroup, NavLeaf } from './navItems.js';

/**
 * TODAY'S FLOOR, BACK BESIDE THE SUMMARY — on the owner's call that Operations
 * belongs under Overview.
 *
 * It was demoted into the Attendance group on the "no siblings" test in
 * `navItems.ts`, and that test still describes the rail correctly elsewhere. What it
 * missed here is what these two rows are FOR: Overview says how the company is
 * doing today and Operations says where its people are right now, and an
 * approver opens one straight after the other all morning. A row you reach by
 * scanning past four calendar screens is not the one beside the summary.
 *
 * It keeps its `sites` pillar and its live `onSiteNow` count; only its filing
 * changes, so there is still exactly one `/operations` destination. Declared
 * here rather than in `navItems.ts` — which is at its line ceiling — and
 * imported into `TOP_LEVEL` there, alongside the `GROUPS` import that already
 * crosses the same way.
 */
export const OPERATIONS: NavLeaf = {
  pillar: 'sites',
  to: '/operations',
  /* OVERVIEW AND OPERATIONS ARE ONE PAGE (owner, 2026-10-15): the Overview's
     figures over this page's map and list. The row takes the Overview's name
     and glyph; `/overview` redirects here — see `AdminOverviewRoute`. */
  labelKey: 'nav.overview',
  label: 'Overview',
  icon: Gauge,
  permission: 'siteops.view',
  live: 'onSiteNow',
};


/* ===========================================================================
 * THE ADMIN SURFACE'S SECTIONS.
 *
 * Out of `navItems.ts` for the reason `tailwind-nav.ts` is out of the preset
 * and `railSkin.ts` is out of `Sidebar.tsx`: this is a coherent unit with one
 * reason to change — what the Admin panel's back-office rail holds — and the
 * file it came from is long-standing size debt behind a ratchet. `navItems.ts`
 * re-exports `GROUPS`, so every importer and every test is unchanged.
 *
 * The type comes back as a TYPE-ONLY import, so nothing here is a runtime
 * cycle: `import type` is erased entirely.
 * ======================================================================== */

/**
 * Management-console back-office — collapsible, role + feature filtered, and
 * ordered by how often the office actually opens each one:
 *   Approvals (somebody is waiting) → Workforce (people records) →
 *   Time & pay (planning + pay) → Administration (set-up, last).
 *
 * FOUR GROUPS, and the count is the discipline. A section earns its heading by
 * holding rows the reader has to choose between; anything that cannot is a
 * child of the page it belongs to.
 *
 * ── WHAT DECIDES LEAF vs GROUP, and why it changed ─────────────────────────
 *
 * The rail used to run FOUR bare destinations (Overview, Operations, Employees,
 * Reports) as peers of FIVE group headings: nine words at the top level, of
 * which four navigate and five expand, with nothing to say which is which. And
 * the taxonomy contradicted itself — `Employees` was a top-level leaf while the
 * group named `People` held Worksites, a team directory and payroll, so the
 * section named after people did not contain the roster and did contain a place
 * register.
 *
 * ONE RULE NOW: a LEAF is a whole-company screen with no siblings to choose
 * between. A GROUP holds rows you must choose between.
 *
 * Overview and Reports pass — the summary and the archive, each unique. The
 * other two did not, and their old justification ("owns an internal navigator")
 * never distinguished them from the rows beside them: Operations is today's
 * floor, which is the sibling of the month; Employees is the roster, which is
 * the sibling of the directory and the site register. Filing them with their
 * siblings takes the top level from nine entries to six for an ADMIN, and from
 * seven to five for a MANAGER, without making one destination less reachable —
 * the row count is unchanged.
 *
 * WHAT THE MOVE DOES NOT TOUCH, and the reason it is safe: `isAdminPath`,
 * `hasAdminAccess` and `allDestinations` all read TOP_LEVEL *and* GROUPS, so
 * moving a destination between the two is inert for surface detection and for
 * the command palette. This is re-filing, not re-wiring.
 *
 * The dividing line between the first three groups and Setup is unchanged:
 * those are where you go to ACT on records, Setup is what a tenant configures
 * once.
 */
export const GROUPS: NavGroup[] = [
  {
    /*
     * THE QUEUES COME FIRST — the only rows here that are somebody waiting.
     *
     * The merged Time & pay note said "action queues (Corrections, Leave
     * approvals) lead", but Corrections was never in the admin surface at all
     * (it lives in the personal list, which admin mode does not render), so the
     * group's first row was Leave approvals and the app's second approval queue
     * could not be reached from the Admin panel.
     */
    id: 'approvals',
    pillar: 'attendance',
    titleKey: 'nav.groups.approvals',
    title: 'Approvals',
    // `Sparkles` is lucide's AI/magic glyph. It said nothing about a queue of
    // people waiting on a decision, and it was drawn on BOTH the section header
    // and one of the rows inside it — so the section and its child were the
    // same picture. A check is what the rows here have in common.
    icon: CheckCheck,
    items: [
      // The approver's queue, not the worker's own request form: `FileSignature`
      // (sign a document) belongs to /corrections in the personal list, which
      // renders alongside this row on a combined-surface console.
      //
      // "Correction APPROVALS", not "Correction requests". Under a heading that
      // says Approvals, three rows read "<thing> approvals" and this one read
      // "<thing> requests" — the worker's word for the object, on the approver's
      // row. One shape for the four (§3.6), and the shape is the one that says
      // what the reader came here to do.
      // THE SIXTH QUEUE, and the one that was not a queue at all until now. A
      // flagged scan WAS reviewable — `FlaggedScanReview`, inside the
      // Operations People panel — but that surface reads `/attendance/live`,
      // which carries each employee's LATEST event and nothing else, so it
      // could show one flagged punch per person and never a day. An approver
      // had no address to go to for "what has not been decided?", and a
      // worker's four off-roster punches had to be found by opening that
      // worker. See `ScanApprovalsPage`.
      //
      // FIRST in the group, not last, because it is the only one of the six
      // whose backlog silently changes what a day says it worked — an
      // undecided scan can sit outside the rollup, and the attendance row
      // above it reads Incomplete with an em-dash in every column. The other
      // five hold a request; this one holds hours.
      //
      // No `feature` gate: the punch path that mints these runs in every
      // deployment. Same three roles as its neighbours, and MANAGER is scoped
      // to direct reports by the service, so the row can never open a queue
      // its reader is refused on.
      { to: '/attendance/scan-approvals', labelKey: 'nav.scanApprovals', label: 'Scan approvals', rail: { labelKey: 'nav.queue.scans', label: 'Scans' }, icon: ScanFace, permission: 'attendance.scans.view', badge: 'scanApprovals', feature: 'faceRecognition' },
      { to: '/corrections/approvals', labelKey: 'nav.correctionApprovals', label: 'Correction approvals', rail: { labelKey: 'nav.queue.corrections', label: 'Corrections' }, icon: FileCheck, permission: 'corrections.approve', feature: 'corrections', badge: 'correctionApprovals' },
      // THE QUEUE HALF OF THE LEAVE PAGE. /leave/manage and /leave/calendar are
      // two addresses of one screen now (see the header of LeaveManagePage.tsx),
      // and this row names the one an approver comes for: it opens on the queue,
      // which is what the badge beside it counts. Two rows for one page is
      // usually the defect the device merge removed — it is right here because
      // each view has its OWN PATH, so each row highlights on its own view and
      // only there, which a `?view=` row could never do. The calendar view has
      // no row of its own, so it lights THIS one (`alsoActiveOn`, owner 2026-10-28).
      { to: '/leave/manage', alsoActiveOn: ['/leave/calendar'], labelKey: 'nav.leaveApprovals', label: 'Leave approvals', rail: { labelKey: 'nav.queue.leave', label: 'Leave' }, icon: ClipboardCheck, permission: 'leave.requests.approve', feature: 'leave', badge: 'leaveApprovals' },
      // THE FOURTH QUEUE, and until now the only one with no door on this
      // surface. A letter request is decided by the same three roles that
      // decide the two rows above, and the queue existed — `LetterApprovalsPanel`
      // behind `?tab=queue`, counted by the dashboard's own decision card — but
      // `/letters` is a PERSONAL row, and admin mode renders no personal rows.
      // So an approver working the Admin panel could reach three of the app's
      // four queues.
      //
      // It points at `/letters/approvals` rather than `?tab=queue` for the
      // reason the Devices note below spells out: `pathMatches` compares `to`
      // against `pathname` with no query awareness, so a query-string row could
      // never highlight. Corrections solved this the same way and this is that
      // solution applied, not a new pattern.
      { to: '/letters/approvals', labelKey: 'nav.letterApprovals', label: 'Letter approvals', rail: { labelKey: 'nav.queue.letters', label: 'Letters' }, icon: FileCheck2, permission: 'letters.approve', feature: 'letters', badge: 'letterApprovals' },
      // THE FIFTH QUEUE, and the one whose absence was worst. The ERP exchange
      // ran BOTH WAYS with nobody in it: a scheduled job wrote the file that
      // pays about a thousand people, and a nightly job rewrote payroll routing
      // from whatever workbook was in a folder. Both now wait on a person, and
      // this is where that person looks.
      //
      // ADMIN/HR, unlike the four rows above it. Leave, corrections and letters
      // are decided by whoever has authority over a WORKER, which is what makes
      // a MANAGER an approver there. Neither ERP object belongs to a team: one
      // is the whole workforce's month, the other is how everybody is paid.
      //
      // Points at the OUTBOUND half, because that is the one with a deadline —
      // the export fires on a configured day and withholds an unapproved batch.
      // An inbound change waits patiently; a payroll file does not.
      { to: '/erp/approvals', labelKey: 'nav.erpApprovals', label: 'ERP approvals', rail: { labelKey: 'nav.queue.erp', label: 'ERP' }, icon: ArrowLeftRight, permission: 'erp.view', feature: 'erp' },
      // THE DEVICE QUEUE, back under Approvals — every queue under one
      // heading, matching the handoff. It used to be filed under
      // Administration on the reasoning that a rail row names its
      // DESTINATION rather than what's inside it, and the destination is a
      // register that CONTAINS a queue rather than being one. Restored here
      // on the owner's call that "where an approver looks for waiting work"
      // outweighs that distinction — Correction requests and Leave approvals
      // are exactly this shape already (a queue view of a bigger page), so
      // Devices being the same shape is consistent, not an exception.
      //
      // This MOVES the one existing row, it does not add a second one: there
      // is still exactly one `/settings/devices` destination and one row
      // pointing at it, just filed here instead of under Administration. Two
      // rows at that path was the thing ruled out — `pathMatches` compares
      // `to` against `pathname` with no query awareness, so a second row at
      // the same bare path would highlight alongside this one on every visit,
      // and a `?status=PENDING` variant would never highlight at all.
      // NAMED FOR THE QUEUE, like its two siblings. The handoff calls this row
      // "Device approvals" (`Hadir Dashboard.dc.html:95`), and under an
      // Approvals heading that is what an approver is looking for. It differs
      // from the page's own "Devices" heading — but so do the two rows above
      // it ("Correction requests" opens *Corrections*, "Leave approvals" opens
      // *Leave*), because in every case the rail names the QUEUE and the page
      // names the REGISTER that contains it. The page keeps "Devices" because
      // it is also a Settings destination, where the register is the point.
      { to: '/settings/devices', labelKey: 'nav.devices', label: 'Device approvals', rail: { labelKey: 'nav.queue.devices', label: 'Devices' }, icon: MonitorSmartphone, permission: 'devices.view', badge: 'deviceApprovals', feature: 'deviceApprovals' },
    ],
  },
  {
    /*
     * ATTENDANCE — the subject this product is about, in one place at last.
     *
     * These four rows lived in three: the calendar and the workspace were
     * top-level, team attendance and the schedule were filed under Workforce
     * because they are about PEOPLE. Every one of them answers "who was where,
     * when", which is the question a reader is holding, and taxonomy is a worse
     * sort key for a rail than the question.
     *
     * THE CALENDAR AND THE WORKSPACE CAME BACK FROM TOP-LEVEL, and the reason
     * they left is now handled rather than avoided: a group that filters down
     * to one row on a lean tenant used to be "a collapsible section you
     * expanded to find one link", so the fix was to promote the row. That fix
     * only ever covered the tenant shape somebody noticed. `NavGroupSection`
     * drops the heading for ANY one-item group now, so this group degrades to a
     * plain row by itself on a tenant with no team, shifts or reports module —
     * for every combination, not the ones we thought of.
     */
    id: 'attendance',
    pillar: 'attendance',
    titleKey: 'nav.groups.attendance',
    title: 'Attendance',
    icon: CalendarCheck,
    items: [
      /* TODAY'S FLOOR IS NO LONGER HERE. Operations went back to the top level,
         beside Overview — see its own note in navItems.ts. This group is the
         month and the plan; the floor is a summary screen, and it reads with
         the summary. Exactly one `/operations` row still exists.

         The month, for the whole workforce, leads instead. Not feature-gated:
         attendance is what this app is, so this row is the one that survives
         every preset and keeps the group from vanishing entirely. */
      { to: '/attendance-calendar', labelKey: 'nav.attendanceCalendar', label: 'Attendance calendar', icon: CalendarCheck, permission: 'attendance.view', pillar: 'attendance' },
      /* `Table2`, not another magnifier: the difference between this row and
         Reports IS that one hands back a figure with its working and the other
         hands back the whole table, and the glyphs should say so before the
         labels do. `pillar: 'reports'` because the screen lives under
         `/reports` and the accent follows the destination, not the group. */
      { to: '/reports/workspace', labelKey: 'nav.reportsWorkspace', label: 'Attendance table', icon: Table2, permission: 'reports.view', feature: 'reports', pillar: 'reports' },
      { to: '/team-attendance', labelKey: 'nav.teamAttendance', label: 'Team attendance', icon: UserCheck, permission: 'attendance.view', feature: 'team' },
      /* THE PLAN, not the register. `/schedule` is who works when, edited
         weekly; `/shifts` is the definitions, written once, and stays in Setup
         with the other reference lists — `navItems.test.ts` pins that split. */
      { to: '/schedule', labelKey: 'nav.schedule', label: 'Schedule', icon: CalendarClock, permission: 'schedules.view', feature: 'shifts' },
      /* THE DAY'S PLAN beside the week's: which site each worker is sent to
         this morning. Behind `dispatch`, which defaults OFF until release. */
      { to: '/dispatch', labelKey: 'nav.dispatch', label: 'Dispatch', icon: Bus, permission: 'dispatch.view', feature: 'dispatch' },
    ],
  },
  {
    /*
     * PEOPLE & PAY — and the roster is finally in it.
     *
     * `Employees` was a top-level leaf on the argument that it is the app's
     * most-opened screen and "a rail that files its busiest destination under a
     * heading is ordering rows by taxonomy rather than by use". The cost of that
     * was a section called People that did not contain the people: a reader
     * looking for the roster opened the group named after it and found a site
     * register, a team directory and payroll.
     *
     * It sits FIRST here, so being in a group costs it no prominence — the
     * groups render expanded, so the row is on screen either way, one heading
     * further down. The name gains "& pay" because payroll prep has been in this
     * group since Time & pay was dissolved and the heading never said so.
     */
    id: 'people',
    pillar: 'people',
    titleKey: 'nav.groups.people',
    title: 'People & pay',
    icon: Users,
    items: [
      { to: '/employees', labelKey: 'nav.employees', label: 'Employees', icon: Users, permission: 'employees.view', pillar: 'people' },
      // BULK IMPORT IS NOT HERE ANY MORE — it is a child of the row above, and
      // the rail was drawing it as that row's sibling. `/employees/import` is
      // reached from the Employees page's own header action, which is where a
      // reader is standing when they decide to load a spreadsheet of people:
      // nobody opens the sidebar to import, they open the list, see it is
      // empty, and import. Listing it here made the flat rail claim a
      // hierarchy the routes contradict, and put a once-a-quarter task beside
      // the app's most-opened screen. It stays in EXTRA_DESTINATIONS so ⌘K
      // still jumps straight to it.
      { to: '/worksites', labelKey: 'nav.worksites', label: 'Worksites', icon: MapPin, permission: 'worksites.view', feature: 'workers' },
      // These two were adjacent rows drawn with the SAME glyph — legible only
      // because the labels were there to tell them apart, which is precisely
      // what the collapsed rail takes away. A directory is a book of people; a
      // team's attendance is a person with a tick.
      { to: '/team', labelKey: 'nav.teamDirectory', label: 'Team directory', icon: BookUser, permission: 'employees.view', feature: 'team' },
      // STAFF TASKS — work handed to people, so it sits with the people.
      // docs/plans/staff-tasks.md. A list with ticks, not a clipboard: the
      // clipboard is the corrections queue's picture.
      { to: '/tasks/manage', labelKey: 'nav.tasks', label: 'Tasks', icon: ListChecks, permission: 'tasks.view', feature: 'tasks', pillar: 'people' },
      /* SCHEDULE AND PAYROLL PREP MOVED HERE, and "Time & pay" is gone.
         That section held three rows: the leave calendar, the schedule and
         payroll prep. The calendar row is retired — it is one VIEW of the leave
         approvals screen, which carries a two-tab strip, so the row was a
         second address for a tab. What was left could not stand up: for a
         MANAGER payroll is ADMIN/HR-only, so the section became a heading, a
         chevron and one link — the exact decay `navItems.test.ts` guards, and
         the reason the calendar row had been kept until now.

         So the SECTION went instead of the row. Both survivors are about the
         subject this group already holds: who your people are, where they work,
         WHEN they work, and what they are paid. Three groups now, and the count
         is still the discipline — a section earns its heading by holding rows
         the reader has to choose between. */
      { to: '/payroll', labelKey: 'nav.payrollPrep', label: 'Payroll prep', icon: Wallet, permission: PAYROLL_PREP_VIEW_KEYS, feature: 'payroll' },
      // Close a month so its figures stop changing, and decide the changes that
      // arrive for it afterwards (audit H-06). Beside Payroll prep: closing is
      // the step after the month's documents are produced.
      { to: '/payroll/periods', labelKey: 'nav.payPeriods', label: 'Pay periods', icon: CalendarRange, permission: 'pay.periods.view', feature: 'payroll' },
      /* THE AUTHORING DOOR ON THE ANNOUNCEMENTS SCREEN, and it was missing.
         
         An HR user published an announcement, saw it render as a banner on the
         Overview, and had no row anywhere in the Admin panel that led back to
         the screen which manages it. `AnnouncementsPage` is role-adapted and
         has been all along — the composer, the audience picker and the manage
         feed are already there for ADMIN/HR — so the only thing absent was a
         door. An authoring surface reachable only by typing its URL is a nav
         defect, not a missing feature.
         
         It points at `/announcements/manage`, a second role-gated PATH onto the
         same component (App.tsx), for the reason the letters queue is at
         `/letters/approvals`: `/announcements` is a PERSONAL row, admin mode
         renders no personal rows, and re-pointing an admin row at the bare path
         would make `isAdminPath()` read it as a management destination — which
         would drop an ordinary worker into the Admin panel the moment they
         opened their own Announcements row.
         
         WORKFORCE, NOT ADMINISTRATION. The line this file draws is between
         acting on records and configuring the tenant once; publishing to the
         workforce is a recurring act aimed at the people this group is about,
         and filing it beside Leave types and Holidays would put a weekly task
         among the reference lists nobody opens twice.
         
         NAMED FOR THE JOB, like every other admin row whose page also has a
         personal one. "Correction approvals" opens *Corrections* and "Letter
         approvals" opens *Letters*; this opens *Announcements* and says what
         the reader came to do. It also cannot be confused with the personal
         reading row on a combined console, where both render — and its glyph
         differs from that row's megaphone for the same reason, because
         collapsed the rail is nothing but glyphs. */
      { to: '/announcements/manage', labelKey: 'nav.manageAnnouncements', label: 'Announcements', icon: MessageSquarePlus, permission: 'announcements.view', feature: 'announcements' },
    ],
  },
  {
    // Everything you configure once and rarely touch: settings, the reference
    // lists (departments/designations/leave types/holidays) and system tooling.
    // Deliberately LAST.
    //
    // It used to claim "and collapsed by default", which stopped being true when
    // the Sidebar moved every group to open-by-default (see the note there —
    // three of the app's commonest tasks were paying a click to reveal a
    // heading's children). A comment describing behaviour the code no longer has
    // is the same defect as a screen describing data it no longer holds.
    id: 'setup',
    pillar: 'admin',
    titleKey: 'nav.groups.setup',
    title: 'Setup',
    defaultClosed: true, // reference lists, set up once (owner, 2026-10-15)
    // The COG, not the wrench. A section heading is the name of what is under
    // it, and this section's anchor row is Settings — the same relationship
    // Workforce has to Employees and Time & pay to Shifts. The wrench said
    // "repairs", which is not what a tenant comes here to do.
    icon: SettingsIcon,
    items: [
      { to: '/settings', labelKey: 'nav.settings', label: 'Settings', icon: SettingsIcon, permission: ANY_SETTINGS_VIEW_KEYS },
      { to: '/departments', labelKey: 'nav.departments', label: 'Departments', icon: Building2, permission: 'departments.view', feature: 'orgStructure' },
      { to: '/designations', labelKey: 'nav.designations', label: 'Designations', icon: IdCard, roles: ['ADMIN', 'HR'], feature: 'orgStructure' },
      { to: '/settings/roles', labelKey: 'nav.rolesAccess', label: 'Roles & access', icon: KeyRound, permission: 'settings.roles.view' },
      { to: '/branches', labelKey: 'nav.branches', label: 'Companies', icon: Landmark, roles: ['ADMIN', 'HR'], permission: 'legalEntities.view', feature: 'legalEntities' },
      { to: '/suppliers', labelKey: 'nav.suppliers', label: 'Labour suppliers', icon: Truck, roles: ['ADMIN', 'HR'], permission: 'laborSuppliers.view', feature: 'labourSuppliers' },
      { to: '/leave/types', labelKey: 'nav.leaveTypes', label: 'Leave types', icon: Tags, roles: ['ADMIN', 'HR'], feature: 'leave' },
      // The shift DEFINITIONS, beside the other reference lists — see the note
      // where this row used to sit, under Time & pay.
      { to: '/shifts', labelKey: 'nav.shifts', label: 'Shifts', icon: Clock, roles: ['ADMIN', 'HR'], feature: 'shifts' },
      { to: '/holidays', labelKey: 'nav.holidays', label: 'Holidays', icon: Palmtree, roles: ['ADMIN', 'HR'], feature: 'leave' },
      /* THE TWO DIAGNOSTICS, back from a System group of their own.
       *
       * Splitting them out was argued on meaning — "the audit log and the job
       * runner are neither reference nor data" — and that reading is still
       * right. What it cost was a whole top-level heading for two ADMIN-only
       * rows, on a rail whose top level was the problem. Meaning loses to the
       * count here, and the rows keep their distinctness by sitting LAST,
       * after the reference lists, which is the order the group is read in.
       *
       * Nobody but an ADMIN ever saw the old heading — both rows are
       * ADMIN-only, so HR and MANAGER rendered an empty group the Sidebar
       * dropped. So this removes a heading that only ever cost the one role
       * that had to scan past it. */
      { to: '/system/audit-log', labelKey: 'nav.auditLog', label: 'Audit log', icon: History, permission: 'system.audit.view' },
      { to: '/system/jobs', labelKey: 'nav.jobs', label: 'Jobs', icon: ServerCog, permission: 'system.jobs.view' },
      // THE DEVICE REGISTER ROW MOVED TO APPROVALS (see the note there) — this
      // group no longer names it. `MonitorSmartphone` and the pending badge
      // both travelled with it; there is still exactly one row and one
      // `/settings/devices` destination, only its section changed.
      // SHIPPING THE FIELD APK USED TO BE A ROW HERE, at `/system/field-app`.
      // It is a tab of the device register now (the owner asked to "combine
      // data from Field app updates"), so the destination it named no longer
      // exists as a page and the row goes with it — §2 keeps exactly one rail
      // row per destination, and the destination is `/settings/devices` under
      // Approvals. A second row pointing at that path would highlight
      // alongside the first on every visit, because `pathMatches` compares
      // `to` against `pathname` with no query awareness; a `?section=app`
      // variant would never highlight at all.
      //
      // The old URL still resolves — App.tsx redirects it onto the tab — so a
      // bookmark or an old link lands where the screen went.
    ],
  },
];
